import { useNow, useDateFormat } from "@vueuse/core";
import { addDays } from "date-fns";

/**
 * Update date based on months (add or subtract)
 *
 * @param {Date} date
 * @param {Number} months
 * @returns {Date} NewDate
 */
export function addMonths(date, months, setDayToFirst = true) {
	var d = date.getDate();
	date.setMonth(date.getMonth() + months);
	if (setDayToFirst && date.getDate() != d) {
		date.setDate(0);
	}
	return date;
}

/**
 * Add months to a date string and return as YYYY-MM-DD format
 * @param {string} dateString - Date string in any valid format (expects YYYY-MM-DD)
 * @param {number} months - Number of months to add (can be negative)
 * @returns {string|null} Date string in YYYY-MM-DD format or null if invalid
 */
export function addMonthsToDateString(dateString, months) {
	if (!dateString) return null;
	
	// Parse the date string as local date to avoid timezone issues
	// If it's in YYYY-MM-DD format, parse it as local date components
	const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (dateMatch) {
		const year = parseInt(dateMatch[1], 10);
		const month = parseInt(dateMatch[2], 10) - 1; // Months are 0-indexed
		const day = parseInt(dateMatch[3], 10);
		const date = new Date(year, month, day);
		if (isNaN(date.getTime())) return null;
		
		const result = addMonths(new Date(date), months, false);
		
		// Format as YYYY-MM-DD using local date components (avoid UTC conversion)
		const resultYear = result.getFullYear();
		const resultMonth = String(result.getMonth() + 1).padStart(2, '0');
		const resultDay = String(result.getDate()).padStart(2, '0');
		return `${resultYear}-${resultMonth}-${resultDay}`;
	}
	
	// Fallback to original behavior for other date formats
	const date = new Date(dateString);
	if (isNaN(date.getTime())) return null;
	const result = addMonths(new Date(date), months, false);
	
	// Format using local date components instead of toISOString() to avoid timezone issues
	const resultYear = result.getFullYear();
	const resultMonth = String(result.getMonth() + 1).padStart(2, '0');
	const resultDay = String(result.getDate()).padStart(2, '0');
	return `${resultYear}-${resultMonth}-${resultDay}`;
}

/**
 * List period in time selection
 *
 * Return current month and previous 3 months plus one month in the future
 * @returns Array of objects with name and value
 */
export function makeDateArray() {
	const dateArray = [];
	const currentY = useDateFormat(useNow(), "YYYY").value;
	const currentM = useDateFormat(useNow(), "M").value;
	const currentD = 1;

	const currentMonth = new Date(currentY, currentM - 1, currentD);

	for (let i = 1; i >= -12; i--) {
		const month = new Date(currentMonth.getTime());
		dateArray.push({
			name: useDateFormat(addMonths(month, i), "MM (MMM) YYYY").value,
			value: useDateFormat(month, "YYYY-MM-DD").value,
		});
	}

	return dateArray;
}

/**
 * Check if Date part of filename structure and return date
 *
 * @param {string} date: In the format MYYYY or MMYYYY
 * @returns
 */
export function makeDateFromFileNameDate(date) {
	let dateString = null;
	if (date.length === 4) {
		dateString = `${date}-01-01`;
	} else if (date.length === 5) {
		const month = date.substring(1, 0);
		const year = date.substring(1, 5);
		dateString = `${year}-0${month}-01`;
	} else if (date.length === 6) {
		const month = date.substring(0, 2);
		const year = date.substring(2, 6);
		dateString = `${year}-${month}-01`;
	}
	return dateString;
}

export function isDateValid(dateStr) {
	if (dateStr === null) return false;
	const date = new Date(dateStr);
	return date instanceof Date && !isNaN(date);
}

export function formatDate(date) {
	if (date) {
		return useDateFormat(new Date(date), "D.M.YYYY", { locales: "de" }).value;
	}
	return "-";
}

export function formatDateFull(date) {
	if (date) {
		return useDateFormat(new Date(date), "DD.MM.YYYY HH:mm:ss", {
			locales: "de",
		}).value;
	}
	return "-";
}

export function fullYearOfDate(date) {
	if (date) {
		return useDateFormat(new Date(date), "YYYY", { locales: "de" }).value;
	}
	return "-";
}

export function formatDateAsPeriod(date) {
	if (date) {
		return useDateFormat(new Date(date), "MM.YYYY", { locales: "de" }).value;
	}
	return "-";
}

export function getAddedFormat(date) {
	return useDateFormat(date, "D.M.YYYY", { locales: "de" }).value;
}

export function getExpiresFormat(date, defaultReturn) {
	if (date) {
		return useDateFormat(new Date(date), "D.M.YYYY", { locales: "de" }).value;
	}
	return defaultReturn ? defaultReturn : "-";
}

export function isFileExpired(date) {
	if (date) {
		const today = new Date();
		const expirationDate = new Date(date);
		//console.log(today, expirationDate);
		return expirationDate.getTime() < today.getTime();
	}
	return false;
}

export function isExpiring(daysInAdvance, date) {
	if (date) {
		const expirationDate = new Date(date);
		const daysFromNow = addDays(new Date(), daysInAdvance);

		return expirationDate.getTime() < daysFromNow.getTime();
	}
	return false;
}

/**
 * Format date for onboarding components (long format with time)
 * Returns format like "January 1, 2024, 12:00 PM"
 * @param {string|Date} dateString - Date string or Date object
 * @returns {string} Formatted date string or empty string if invalid
 */
export function formatDateForOnboarding(dateString) {
	if (!dateString) return ''
	
	try {
		const date = new Date(dateString)
		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		})
	} catch (e) {
		return dateString
	}
}
